import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, RefreshCw } from "lucide-react";

import StudentSearchField from "../../components/StudentSearchField";
import StudentFinancialSummary from "../../components/StudentFinancialSummary";
import { documentLink, documentStatus, num, orderDocuments, receiptConfig, ugx, viewerNote, type BursarDesk, type Receipt } from "../../components/bursar/bursarLogic";
import { DecisionsCard, ViewerNote } from "../../components/dashboard/DeskParts";
import { longDay, shortDay } from "../../components/dashboard/deskLogic";
import { Field } from "../../components/FormLayout";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import { api } from "../../services/api";

/**
 * The bursar's dashboard: the working day. What is waiting on them, their own
 * documents in the approval workflow, the day's collections to cash up, where
 * the money is, and a learner's account at a glance. The period-wide picture
 * stays on the finance overview.
 */
export default function BursarDashboard() {
  const [day, setDay] = useState("");
  const [studentId, setStudentId] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["bursar-desk", day],
    queryFn: async () => (await api.get("/dashboard/bursar/", { params: day ? { date: day } : {} })).data as BursarDesk,
    keepPreviousData: true,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the bursar's dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The bursar's dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const collections = data.collections;
  const isToday = collections.day === data.today;
  const documents = orderDocuments(data.documents);
  const returned = documents.filter((doc) => doc.workflow_status === "returned").length;

  const columns: RegisterColumn<Receipt>[] = [
    { header: "Time", cell: (row) => <span className="tabular-nums">{row.time}</span>, exportValue: (row) => row.time },
    { header: "Receipt", cell: (row) => <span className="font-medium text-gray-900">{row.receipt_number || "—"}</span>, exportValue: (row) => row.receipt_number },
    { header: "Learner", cell: (row) => <span><span className="block text-gray-900">{row.student}</span><span className="text-xs text-gray-500">{[row.admission_number, row.class_name].filter(Boolean).join(" · ")}</span></span>, exportValue: (row) => row.student },
    { header: "Class", cell: () => "", exportValue: (row) => row.class_name, exportOnly: true },
    { header: "Method", cell: (row) => row.method_label, exportValue: (row) => row.method_label },
    { header: "Reference", cell: (row) => row.reference || "—", exportValue: (row) => row.reference },
    { header: "Received", cell: (row) => ugx(row.amount), exportValue: (row) => num(row.amount), align: "right" },
    { header: "Refunded", cell: (row) => (num(row.refunded) ? <span className="text-red-700">{ugx(row.refunded)}</span> : "—"), exportValue: (row) => num(row.refunded), align: "right" },
    { header: "Into account", cell: () => "", exportValue: (row) => row.account, exportOnly: true },
    { header: "Invoice", cell: () => "", exportValue: (row) => row.invoice, exportOnly: true },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bursar</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · the day's work, collections and cash.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/financial" className="btn btn-outline gap-1.5">Finance overview <ArrowRight className="h-4 w-4" /></Link>
          <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <DecisionsCard decisions={data.work} title="Work waiting" />
        </div>
        <section className="card">
          <div className="card-titlebar">
            <div>
              <h2 className="font-semibold text-gray-900">Your documents</h2>
              <p className="text-xs text-gray-500">{returned ? `${returned} sent back to you for changes` : "In the approval workflow"}</p>
            </div>
            <Link to="/my-approvals" className="text-xs font-medium text-primary-700 hover:underline">My approvals</Link>
          </div>
          {documents.length ? (
            <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
              {documents.map((doc) => (
                <li key={`${doc.document_type}-${doc.id}`}>
                  <Link to={documentLink(doc.tab)} className="block px-6 py-2.5 hover:bg-gray-50">
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0 text-sm font-medium text-gray-900">{doc.title}</span>
                      {doc.amount && <span className="shrink-0 text-xs tabular-nums text-gray-600">{ugx(doc.amount)}</span>}
                    </span>
                    <span className="mt-0.5 flex items-center justify-between gap-3"><StatusDot {...documentStatus(doc)} />{doc.updated_at && <span className="text-xs text-gray-400">{shortDay(doc.updated_at)}</span>}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">Nothing of yours is waiting in the workflow.</p>}
        </section>
      </div>

      <section className={`space-y-4 transition-opacity ${query.isFetching ? "opacity-70" : ""}`}>
        <div className="card card-content flex flex-wrap items-end justify-between gap-4 pt-5">
          <Field label="Collections for" className="w-full sm:w-48">
            <input type="date" className="input mt-1" value={collections.day} max={data.today} onChange={(event) => setDay(event.target.value)} />
          </Field>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <p className="text-xs font-medium text-gray-500">{isToday ? "Received today" : `Received ${shortDay(collections.day)}`}</p>
              <p className="text-2xl font-semibold tabular-nums text-gray-900">{ugx(collections.total)}</p>
              <p className="text-xs text-gray-500">{collections.receipts.length} {collections.receipts.length === 1 ? "receipt" : "receipts"}, net of refunds</p>
            </div>
            {collections.by_method.map((method) => (
              <div key={method.method}>
                <p className="text-xs font-medium text-gray-500">{method.label}</p>
                <p className="text-lg font-semibold tabular-nums text-gray-900">{ugx(method.amount)}</p>
                <p className="text-xs text-gray-500">{method.count} {method.count === 1 ? "receipt" : "receipts"}</p>
              </div>
            ))}
            <div className="border-l border-gray-200 pl-6">
              <p className="text-xs font-medium text-gray-500">This month so far</p>
              <p className="text-lg font-semibold tabular-nums text-gray-900">{ugx(data.collected_this_month)}</p>
            </div>
          </div>
        </div>

        <ClientRegister
          key={collections.day}
          title={isToday ? "Today's receipts" : `Receipts, ${longDay(collections.day)}`}
          description="Every fee receipt for the day, to count the cash and match mobile money and bank lines before banking. The export is the day's cash-up sheet."
          actions={<Link to="/financial/payments" className="btn btn-outline h-9 text-sm">All payments</Link>}
          rows={collections.receipts}
          config={receiptConfig}
          columns={columns}
          rowKey={(row) => row.id}
          noun={["receipt", "receipts"]}
          exportName={{ file: `receipts-${collections.day}`, title: `Receipts, ${longDay(collections.day)}` }}
          emptyText={isToday ? "No fees received yet today." : "No fees were received that day."}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card">
          <div className="card-titlebar"><h2 className="flex items-center gap-2 font-semibold text-gray-900"><Landmark className="h-4 w-4 text-gray-500" /> Where the money is</h2><Link to="/financial/banking" className="text-xs font-medium text-primary-700 hover:underline">Banking</Link></div>
          {data.money_accounts.length ? (
            <ul className="divide-y divide-gray-100">
              {data.money_accounts.map((account) => (
                <li key={account.id} className="flex justify-between gap-3 px-6 py-2.5 text-sm">
                  <span><span className="tabular-nums text-gray-500">{account.code}</span> {account.name}</span>
                  <span className={`font-semibold tabular-nums ${num(account.balance) < 0 ? "text-red-700" : "text-gray-900"}`}>{ugx(account.balance)}</span>
                </li>
              ))}
              <li className="flex justify-between gap-3 bg-gray-50 px-6 py-2.5 text-sm font-semibold"><span>Total</span><span className="tabular-nums">{ugx(data.money_accounts.reduce((sum, account) => sum + num(account.balance), 0))}</span></li>
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No cash or bank accounts are set up in the chart of accounts yet.</p>}
          <p className="border-t border-gray-100 px-6 py-2 text-xs text-gray-500">Ledger balances as at today, from posted entries.</p>
        </section>

        <section className="card lg:col-span-2">
          <div className="card-titlebar"><h2 className="font-semibold text-gray-900">A learner's account</h2></div>
          <div className="card-content space-y-3 pt-4">
            <p className="text-sm text-gray-500">Find any learner to see what they have been billed, what they have paid and what they still owe.</p>
            <div className="max-w-md">
              <StudentSearchField name="dashboard-student" required={false} onSelect={(student) => setStudentId(student?.id ?? null)} />
            </div>
            {studentId ? <StudentFinancialSummary studentId={studentId} eventListClassName="mt-3 max-h-64 overflow-y-auto text-sm" /> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
